import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { Typography } from 'Components/Typography';

import { moduleClinical } from 'assets/content/landingPages/supplements';
import afterPictureDesktop from 'assets/images/landingPages/supplements/d-after_3_months.jpg';
import beforePictureDesktop from 'assets/images/landingPages/supplements/d-before_1_month.jpg';
import afterPictureMobile from 'assets/images/landingPages/supplements/m-after_3_months.jpg';
import beforePictureMobile from 'assets/images/landingPages/supplements/m-before_1_month.jpg';

const BeforeAfterWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 56px 0;

  ${legacyTheme.breakpoints.up('md')} {
    flex-direction: row;
    padding: 80px 0;

    & > *:first-of-type {
      margin-right: 144px;
    }
  }
`;

const BeforeAfterHeader = styled.div`
  margin-bottom: 47px;

  ${legacyTheme.breakpoints.up('sm')} {
    margin-bottom: 56px;
  }
`;

const Title = styled(Typography)`
  margin-bottom: 12px;

  font-size: 36px;
  line-height: 1.17;
  letter-spacing: 0.25px;

  ${legacyTheme.breakpoints.up('sm')} {
    max-width: 500px;
    margin-bottom: 32px;
    font-size: 60px;
    letter-spacing: normal;
  }

  ${legacyTheme.breakpoints.up('md')} {
    margin-bottom: 0;
  }
`;

const Paragraph = styled(Typography)`
  line-height: 1.5;
  letter-spacing: 0.25px;

  ${legacyTheme.breakpoints.up('md')} {
    max-width: 500px;
    margin-top: 25px;
  }
`;

const WidthBox = styled.div`
  max-width: 360px;

  ${legacyTheme.breakpoints.up('md')} {
    max-width: 488px;
  }
`;

const FigureList = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-self: center;
  justify-content: space-between;
  gap: 1px;

  border-color: black;
`;

const FigureWrapper = styled.figure`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 2;
  width: 50%;
  max-width: 244px;
  margin: 0;

  background-color: ${theme.colors.primary[400]};
`;

const FigureImage = styled.img`
  object-fit: cover;
  max-width: 100%;
`;

const FigureLegend = styled.div`
  display: grid;
  place-items: center;
  min-height: 37px;
`;

const FigureListLegend = styled.div`
  display: grid;
  place-items: center;
  min-height: 37px;

  border: solid 1.5px ${theme.colors.primary[400]};
`;

const Legend = styled(Typography)`
  letter-spacing: 0.5px;

  &:first-of-type {
    margin-bottom: 14px;
    line-height: 1.5;
  }

  ${legacyTheme.breakpoints.up('sm')} {
    text-align: center;
  }

  ${legacyTheme.breakpoints.up('md')} {
    &:first-of-type {
      margin-bottom: 16px;
    }
  }

  & a {
    font-weight: 600;
    text-decoration: none;
  }

  & sup {
    font-size: 70%;
  }
`;

export const BeforeAfter = () => (
  <>
    <BeforeAfterWrapper>
      <BeforeAfterHeader>
        <Title markupName="p" variant="p1">
          {moduleClinical.title2}
        </Title>

        <Paragraph markupName="p" variant="p1">
          {moduleClinical.paragraph2}
        </Paragraph>
      </BeforeAfterHeader>

      <WidthBox>
        <FigureList>
          <FigureWrapper>
            <FigureImage
              alt="scalp before 1 month of supplements"
              sizes="(min-width: 1024px) 244px, 179px"
              src={beforePictureMobile.src}
              srcSet={`${beforePictureDesktop.src} 488w, ${beforePictureMobile.src} 358w`}
            />
            <FigureLegend>
              <Typography color="white" markupName="figcaption" variant="mono3">
                1 month
              </Typography>
            </FigureLegend>
          </FigureWrapper>

          <FigureWrapper>
            <FigureImage
              alt="scalp after 3 months of supplements"
              sizes="(min-width: 1024px) 244px, 179px"
              src={afterPictureMobile.src}
              srcSet={`${afterPictureDesktop.src} 488w, ${afterPictureMobile.src} 358w`}
            />
            <FigureLegend>
              <Typography color="white" markupName="figcaption" variant="mono3">
                3 months
              </Typography>
            </FigureLegend>
          </FigureWrapper>
        </FigureList>

        <FigureListLegend>
          <Typography align="center" color="noir" variant="mono3">
            Sarah has type 1 straight hair
          </Typography>
        </FigureListLegend>
      </WidthBox>
    </BeforeAfterWrapper>

    <Legend markupName="p" variant="p3">
      {moduleClinical.legend}
    </Legend>
  </>
);
